import { components } from '@doltech/dto';
import { SkillEnum, TestTypeEnum } from '@doltech/utils/lib/constants';
import { useCreateStore, useProvider, useStore } from 'mobx-store-provider';
import * as React from 'react';

import {
  ReviewStatus,
  TestSubmissionStore,
  TestSubmissionStoreInstance,
  TestSubmissionStoreSnapshotIn,
} from './test-submissions.store';

const IMG_PLACE_HOLDER = 'https://dummyimage.com/48x48.png';

interface GetInitialStateOfTestSubmissionStoreParams {
  data: components['schemas']['UserFullTestDTO'] | components['schemas']['UserPracticeTestDTO'];
  isPreviewMode?: boolean;
}

type PreviewDataFromTestDetail = {
  data: any;
  result: any;
};

const getPreviewDataFromFullTestDetail = ({ data, result }: PreviewDataFromTestDetail) => {
  result.name = data?.test?.name || data?.name;
  result.skill = data?.test?.skill || data?.skill;
  result.testID = data?.test?.id || data?.id;
  result.bookCover = data.test?.coverImage?.url;
  const testSections = data?.test?.testSections || data?.testSections || [];
  result.testSections = testSections;
  result.totalSections = testSections.length;
  result.audio = data?.test?.audio;

  let duration = 0;
  switch (result.skill) {
    case SkillEnum.READING: {
      duration = testSections.reduce((p, c) => p + c.duration, 0);
      break;
    }
    case SkillEnum.LISTENING: {
      duration = testSections[0].duration;
      break;
    }
  }

  result.totalDuration = duration * 60;
  result.inProgressTotalDurationInSeconds = result.totalDuration;
};

type TestDataFromUserFullTest = {
  data: any;
  result: any;
};
const getTestDataFromUserFullTest = ({ data, result }: TestDataFromUserFullTest) => {
  const testData = data;
  result.name = testData?.test?.name;
  result.skill = testData?.test?.skill as SkillEnum;
  if (testData.test?.skill === SkillEnum.LISTENING) {
    if (testData.currentActiveSection < 0) {
      result.reviewStatus = ReviewStatus.REVIEWING;
      result.reviewedTime = testData.reviewedTime;
      result.listeningGuideConfirmModalVisible = false;
      result.currentSectionIndex = 0;
    } else {
      result.currentSectionIndex = testData.currentActiveSection;
      result.currentAudioIndex = testData.currentActiveSection;
    }
  }
  result.bookCover = testData.test?.coverImage?.url;
  result.totalDuration = testData.test?.totalDuration * 1000;
  result.totalDurationInSeconds = testData.test?.totalDuration;

  result.reviewTime = testData.test?.reviewTime * 1000;
  result.totalSections = testData.testSections?.length;
  result.inProgressTotalDurationInSeconds =
    testData.test?.totalDuration - (testData.testTakenTimeInSeconds || 0);
  result.remainTimeInSeconds = result.inProgressTotalDurationInSeconds;
  result.remainReviewTimeInSeconds = testData.test?.reviewTime;
  result.testSections = testData.testSections;
  result.testTakenTimeInSeconds = testData.testTakenTimeInSeconds;
  result.isPreviewMode = false;
  result.audio = testData.test?.audio;
};

export const getInitialStateOfTestSubmissionStore = ({
  data,
  isPreviewMode = false,
}: GetInitialStateOfTestSubmissionStoreParams) => {
  const result: TestSubmissionStoreSnapshotIn = {
    initialed: true,
    timeUpModalVisible: false,
    submitModalVisible: false,
    sectionTimeUpModalVisible: false,
    submitWarnModalVisible: false,
    bookCover: IMG_PLACE_HOLDER,

    createdAt: data.createdAt,
    id: data.id,
    lastModifiedAt: data.lastModifiedAt,
    testType: data.testType as any,
    isPreviewMode,
  };

  switch (data.testType) {
    case TestTypeEnum.FULL_TEST: {
      if (isPreviewMode) {
        const testData = data as any;
        getPreviewDataFromFullTestDetail({ result, data: testData });
      } else {
        getTestDataFromUserFullTest({ result, data });
      }
      break;
    }
    case TestTypeEnum.PRACTICE_TEST: {
      const testData = data as components['schemas']['UserPracticeTestDTO'];
      result.testID = testData.testSection?.id;
      result.name = testData.testSection?.name;
      result.bookCover = testData.testSection?.coverImage?.url;
      result.skill = testData.testSection?.skill as SkillEnum;
      result.totalDurationInSeconds = testData.testSection.duration * 60;

      if (!isPreviewMode) {
        result.totalDuration = testData.testSection.duration * 60 * 1000;
        result.remainTimeInSeconds =
          testData.testSection.duration * 60 - testData.testTakenTimeInSeconds;
      } else {
        result.totalDuration = testData.testSection.duration * 60 * 1000;
        result.remainTimeInSeconds = testData.testSection.duration * 60;
      }
      result.inProgressTotalDurationInSeconds = result.remainTimeInSeconds; // this is to display on time limit
      result.totalSections = 1;
      result.testSections = [testData.testSection];
      result.bottomNavigationExpanded = false;
      result.testTakenTimeInSeconds = testData.testTakenTimeInSeconds;
      break;
    }
    default:
  }

  return result;
};

export const useTestSubmissionStore = (): TestSubmissionStoreInstance => {
  return useStore(TestSubmissionStore);
};

export const withMockTestSubmissionStore = (Component: any) => (props: any) => {
  const doTestCreateStore = useCreateStore(
    TestSubmissionStore,
    getInitialStateOfTestSubmissionStore({
      data: {
        userId: 'none',
      },
    })
  );
  const DoTestStoreProvider = useProvider(TestSubmissionStore);

  return (
    <DoTestStoreProvider value={doTestCreateStore}>
      <Component {...props} />
    </DoTestStoreProvider>
  );
};

export const withTestSubmissionStore = (Component: any) => (props: any) => {
  const doTestCreateStore = useCreateStore(TestSubmissionStore);
  const DoTestStoreProvider = useProvider(TestSubmissionStore);

  return (
    <DoTestStoreProvider value={doTestCreateStore}>
      <Component {...props} />
    </DoTestStoreProvider>
  );
};
